import { useAuth } from "@/hooks/useAuth";
import { useAddDomain, useDomains, useRemoveDomain } from "@/hooks/useDomains";
import { useSetting } from "@/hooks/useSettings";
import { useTenant } from "@/hooks/useTenant";
import {
  useCreateGroup,
  useDeleteGroup,
  useRenameGroup,
  useUserGroups,
} from "@/hooks/useUserGroups";
import {
  useBulkImportUsers,
  useCreateUser,
  useDeleteUser,
  useUpdateUserRole,
  useUsers,
  type PortalMember,
} from "@/hooks/useUsers";
import { parseCsvUsers } from "@/lib/csvParser";
import { useState } from "react";

// ─── Confirmation dialog hook ────────────────────────────────────────────────

export function useConfirmDialog() {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<(() => void) | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const show = (t: string, desc: string, fn: () => void) => {
    setTitle(t);
    setDescription(desc);
    setAction(() => fn);
    setOpen(true);
  };

  return { open, setOpen, action, title, description, show };
}

// ─── Data + action handlers hook ─────────────────────────────────────────────

// eslint-disable-next-line max-lines-per-function -- SIM-1760: aggregator hook composes ~12 react-query mutations + 4 paired confirm/handle blocks for users / groups / domains / CSV. Splitting would scatter related handlers across files for no readability gain; AdminUsers surface is low-churn admin tooling.
export function useUserManagementData(
  confirm: ReturnType<typeof useConfirmDialog>,
  setError: (msg: string | null) => void,
) {
  const { user } = useAuth();
  const { currentTenantId } = useTenant();

  const { users, isLoading: usersLoading } = useUsers();
  const createUser = useCreateUser();
  const bulkImport = useBulkImportUsers();
  const deleteUser = useDeleteUser();
  const updateUserRole = useUpdateUserRole();

  const { domains, isLoading: domainsLoading } = useDomains();
  const addDomain = useAddDomain();
  const removeDomain = useRemoveDomain();

  const { data: groups, isLoading: groupsLoading } = useUserGroups();
  const createGroup = useCreateGroup();
  const renameGroup = useRenameGroup();
  const deleteGroup = useDeleteGroup();

  const { value: defaultGroupId, save: saveDefaultGroupId } = useSetting("default_group_id");

  const handleRemoveUser = (userId: string) => {
    if (userId === user?.id) {
      setError("You cannot remove yourself.");
      return;
    }
    const targetUser = users.find((u) => u.userId === userId);
    confirm.show(
      "Remove User",
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty-string email falls through to "this user"
      `Are you sure you want to remove ${targetUser?.email || "this user"} from this tenant? They will lose access to all dashboards.`,
      () => {
        void (async () => {
          setError(null);
          try {
            await deleteUser.mutateAsync(userId);
          } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to remove user");
          }
        })();
      },
    );
  };

  const handleDeleteGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    confirm.show(
      "Delete Group",
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty-string name falls through to "this group"
      `Are you sure you want to delete the group "${group?.name || "this group"}"? All members will be removed from this group and any dashboard assignments to this group will be lost.`,
      () => {
        void (async () => {
          setError(null);
          try {
            await deleteGroup.mutateAsync(groupId);
          } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to delete group");
          }
        })();
      },
    );
  };

  const mutateWithError = async (fn: () => Promise<unknown>, msg: string) => {
    setError(null);
    try {
      await fn();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : msg);
    }
  };

  return {
    user,
    users,
    usersLoading,
    groups,
    groupsLoading,
    domains,
    domainsLoading,
    defaultGroupId,
    saveDefaultGroupId,
    createUser,
    updateUserRole,
    createGroup,
    renameGroup,
    handleRemoveUser,
    handleDeleteGroup,
    handleRenameGroup: (groupId: string, name: string) =>
      mutateWithError(() => renameGroup.mutateAsync({ groupId, name }), "Failed to rename group"),
    handleAddDomain: (d: string) =>
      mutateWithError(() => addDomain.mutateAsync(d), "Failed to add domain"),
    handleRemoveDomain: (id: string) =>
      mutateWithError(() => removeDomain.mutateAsync(id), "Failed to remove domain"),
    handleCreateGroup: (name: string) =>
      mutateWithError(() => createGroup.mutateAsync(name), "Failed to create group"),
    handleCsvUpload: async (csvFile: File) => {
      if (!currentTenantId) return;
      setError(null);
      const text = await csvFile.text();
      const parsed = parseCsvUsers(text);
      if (parsed.error) {
        setError(parsed.error);
        return;
      }
      if (parsed.users.length === 0) return;
      await bulkImport.mutateAsync(parsed.users);
    },
  };
}

// ─── Group name dialog hook (create + rename) ────────────────────────────────

/**
 * Owns the create/rename group dialog state (SIM-2553). Returns ready-to-spread
 * props so the orchestrator adds no branching of its own — `UserManagement` is
 * already at the `complexity` cap.
 */
export function useGroupDialog(data: {
  createGroup: { isPending: boolean };
  renameGroup: { isPending: boolean };
  handleCreateGroup: (name: string) => Promise<void>;
  handleRenameGroup: (groupId: string, name: string) => Promise<void>;
}) {
  const [target, setTarget] = useState<
    { mode: "create" } | { mode: "rename"; groupId: string; name: string } | null
  >(null);

  return {
    // GroupDialog seeds its input from `initialName` via useState, so it must be
    // remounted per target rather than synced with an effect.
    dialogKey: target?.mode === "rename" ? `rename-${target.groupId}` : "create",
    open: target !== null,
    mode: target?.mode ?? "create",
    initialName: target?.mode === "rename" ? target.name : "",
    isPending: data.createGroup.isPending || data.renameGroup.isPending,
    openCreate: () => setTarget({ mode: "create" }),
    openRename: (group: { id: string; name: string }) =>
      setTarget({ mode: "rename", groupId: group.id, name: group.name }),
    onOpenChange: (open: boolean) => {
      if (!open) setTarget(null);
    },
    onSubmit: (name: string) => {
      void (async () => {
        if (!target) return;
        if (target.mode === "rename") {
          await data.handleRenameGroup(target.groupId, name);
        } else {
          await data.handleCreateGroup(name);
        }
        setTarget(null);
      })();
    },
  };
}

// ─── Add/edit user dialog hook ───────────────────────────────────────────────

export function useAddEditUserDialog(
  createUser: ReturnType<typeof useCreateUser>,
  updateUserRole: ReturnType<typeof useUpdateUserRole>,
  setError: (msg: string | null) => void,
) {
  const [open, setOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("viewer");

  const openAdd = () => {
    setEditingUserId(null);
    setName("");
    setEmail("");
    setRole("viewer");
    setOpen(true);
  };

  const openEdit = (u: PortalMember) => {
    setEditingUserId(u.userId);
    setName(u.name ?? "");
    setEmail(u.email);
    setRole(u.role);
    setOpen(true);
  };

  const confirm = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }
    setError(null);
    try {
      if (editingUserId) {
        await updateUserRole.mutateAsync({
          userId: editingUserId,
          role: role as "viewer" | "admin",
        });
      } else {
        const trimmedName = name.trim();
        await createUser.mutateAsync({
          email: trimmedEmail,
          name: trimmedName || undefined,
          role: role as "viewer" | "admin",
        });
      }
      setOpen(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    }
  };

  return {
    open,
    setOpen,
    editingUserId,
    name,
    setName,
    email,
    setEmail,
    role,
    setRole,
    openAdd,
    openEdit,
    confirm,
    isPending: createUser.isPending || updateUserRole.isPending,
  };
}
